'use client';

import FaIcon, { faChevronCircleDown, faChevronDown, faChevronUp } from '@/components/icon/FaIcon/FaIcon';
import Accordion from '@/components/ui/Accordion/Accordion';
import { getLocalizedText } from '@/lib/l18n/function';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { scrollIntoTheView } from '@/utils/functions';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './LotteryDetailDescription.module.scss';

function LotteryDetailDescription({
  precautions,
  description,
  shippingDescription,
  benefitDescription,
  wchanceAbout,
  precautions_en,
  description_en,
  shippingDescription_en,
  benefitDescription_en,
  wchanceAbout_en,
  descriptionTitle = 'くじ詳細',
  descriptionTitle_en = 'Lottery Details',
  howToId,
  howToTradeId,
  isDefaultOpen = false,
  isFamikuji = false,
  isEnabledTrade = false,
}: {
  precautions: string;
  description: string;
  shippingDescription: string;
  benefitDescription?: string;
  wchanceAbout?: string;
  precautions_en?: string;
  description_en?: string;
  shippingDescription_en?: string;
  benefitDescription_en?: string;
  wchanceAbout_en?: string;
  descriptionTitle?: string;
  descriptionTitle_en?: string;
  howToId?: string;
  howToTradeId?: string;
  isDefaultOpen?: boolean;
  isFamikuji?: boolean;
  isEnabledTrade?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [isOpened, setIsOpened] = useState(isDefaultOpen);
  const [transLateItem, setTransLateItem] = useState({
    title: '',
    description: '',
    precautions: '',
    shippingDescription: '',
    benefitDescription: '',
    wchanceAbout: '',
  });
  const [contentUpdated, setContentUpdated] = useState(0);

  useEffect(() => {
    setTransLateItem({
      title: getLocalizedText(i18n.resolvedLanguage, { ja: descriptionTitle, en: descriptionTitle_en ?? '' }),
      description: getLocalizedText(i18n.resolvedLanguage, { ja: description, en: description_en ?? '' }),
      precautions: getLocalizedText(i18n.resolvedLanguage, { ja: precautions, en: precautions_en ?? '' }),
      shippingDescription: getLocalizedText(i18n.resolvedLanguage, {
        ja: shippingDescription,
        en: shippingDescription_en ?? '',
      }),
      benefitDescription: getLocalizedText(i18n.resolvedLanguage, {
        ja: benefitDescription ?? '',
        en: benefitDescription_en ?? '',
      }),
      wchanceAbout: getLocalizedText(i18n.resolvedLanguage, { ja: wchanceAbout ?? '', en: wchanceAbout_en ?? '' }),
    });

    setContentUpdated((prev) => prev + 1);
  }, [
    i18n.resolvedLanguage,
    descriptionTitle,
    descriptionTitle_en,
    description,
    description_en,
    precautions,
    precautions_en,
    shippingDescription,
    shippingDescription_en,
    benefitDescription,
    benefitDescription_en,
    wchanceAbout,
    wchanceAbout_en,
  ]);

  const toggleAccordion = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpened(!isOpened);
  };
  const goHowTo = (e: React.MouseEvent, targetId: string) => {
    e.preventDefault();
    scrollIntoTheView(targetId);
  };
  const accordionKey = useMemo(() => contentUpdated, [contentUpdated]);

  const isDescriptionHidden =
    !transLateItem.description || transLateItem.description.trim() === '' || transLateItem.description === '<p>　</p>';

  return (
    <>
      <div className={styles.componentStyle}>
        <p className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>{transLateItem.title}</p>
        <hr className={styles.itemHr} />
        <div className={utilsStyles.textSmall}>
          <Accordion isOpened={isOpened} key={accordionKey} startHeight={50}>
            <div
              className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml} ${isDescriptionHidden ? styles.hidden : ''}`}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{ __html: transLateItem.description }}
            />
            {precautions && (
              <>
                <p className={`${isDescriptionHidden ? '' : utilsStyles.mtXSmall} ${utilsStyles.textMedium}`}>
                  <b>{t('precautions')}</b>
                </p>
                <div
                  className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                  dangerouslySetInnerHTML={{ __html: transLateItem.precautions }}
                />
              </>
            )}
            {shippingDescription && (
              <>
                <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textMedium}`}>
                  <b>{t('shippingInstructions')}</b>
                </p>
                <div
                  className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                  dangerouslySetInnerHTML={{ __html: transLateItem.shippingDescription }}
                />
              </>
            )}
            {benefitDescription && (
              <>
                <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textMedium}`}>
                  <b>{t('benefits')}</b>
                </p>
                <div
                  className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                  dangerouslySetInnerHTML={{ __html: transLateItem.benefitDescription }}
                />
              </>
            )}
            {wchanceAbout && (
              <>
                <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textMedium}`}>
                  <b>{t('wChance')}</b>
                </p>
                <div
                  className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                  dangerouslySetInnerHTML={{ __html: transLateItem.wchanceAbout }}
                />
              </>
            )}
          </Accordion>

          <div className={utilsStyles.centered}>
            {/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
            <a
              href='#'
              onClick={(e) => {
                toggleAccordion(e);
              }}
            >
              {isOpened ? t('close') : t('showMore')}
            </a>
            {isOpened ? <FaIcon icon={faChevronUp} /> : <FaIcon icon={faChevronDown} />}
          </div>
        </div>
      </div>
      {howToId && (
        <div className={`${styles.linkContainer} ${utilsStyles.mtMedium}`}>
          {isFamikuji && (
            <Link
              href='#'
              onClick={(e) => {
                goHowTo(e, howToId);
              }}
              className={utilsStyles.textLink}
            >
              t('howToPlayLottery')
            </Link>
          )}
          <button
            type='button'
            className={styles.raffleButton}
            onClick={(e) => {
              goHowTo(e, howToId);
            }}
          >
            <FaIcon icon={faChevronCircleDown} size='lg' /> RAFFLEの利用方法
          </button>
          {isEnabledTrade && howToTradeId && (
            <button
              type='button'
              className={styles.tradeButton}
              onClick={(e) => {
                goHowTo(e, howToTradeId);
              }}
            >
              <FaIcon icon={faChevronCircleDown} size='lg' /> 景品交換の利用方法
            </button>
          )}
        </div>
      )}
    </>
  );
}

export default LotteryDetailDescription;
